/**
 * Number formatting utilities for displaying large numbers with K/M/B/T suffixes
 */

/**
 * Format a number with appropriate suffix (K, M, B, T)
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted number with suffix
 */
export const formatNumber = (num, decimals = 1) => {
  if (num === 0) return '0';
  if (num < 0) return `-${formatNumber(Math.abs(num), decimals)}`;

  const absNum = Math.abs(num);
  
  // Check if the number is in exponential notation (as a string or when converted)
  const numStr = String(num);
  if (numStr.includes('e') || numStr.includes('E')) {
    // Parse exponential notation
    const match = numStr.match(/^([+-]?\d*\.?\d+)[eE]([+-]?\d+)$/);
    if (match) {
      const base = parseFloat(match[1]);
      const exponent = parseInt(match[2]);
      // Format base to 1 decimal place, but keep .0 if it's exactly .0
      const formattedBase = base.toFixed(1);
      return `${formattedBase}E${exponent}`;
    }
  }
  
  // For extremely large numbers (>= 1e20) that aren't already in exponential notation, use E notation
  if (absNum >= 1e20) {
    const exp = Math.floor(Math.log10(absNum));
    const base = num / Math.pow(10, exp);
    const formattedBase = base.toFixed(1);
    return `${formattedBase}E${exp}`;
  }
  
  // For truncated values (K, M, B, T, Q, Qn, Sx), always show 1 decimal place (ignore decimals parameter)
  if (absNum >= 1e21) {
    return (num / 1e21).toFixed(1) + 'Sx';
  }
  if (absNum >= 1e18) {
    return (num / 1e18).toFixed(1) + 'Qn';
  }
  if (absNum >= 1e15) {
    return (num / 1e15).toFixed(1) + 'Q';
  }
  if (absNum >= 1e12) {
    return (num / 1e12).toFixed(1) + 'T';
  }
  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  }
  if (absNum >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  }
  if (absNum >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  }
  
  // For numbers less than 1000, show up to 2 decimal places if needed
  if (absNum < 1) {
    return num.toFixed(2).replace(/\.?0+$/, '');
  }
  
  return Math.round(num).toLocaleString();
};

/**
 * Format a currency value with appropriate suffix
 * @param {number} value - The currency value to format
 * @param {string} currency - Currency symbol (default: '$')
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted currency with suffix
 */
export const formatCurrency = (value, currency = '$', decimals = 1) => {
  if (value === 0) return `${currency}0`;
  
  const formatted = formatNumber(value, decimals);
  return `${currency}${formatted}`;
};

/**
 * Format a percentage value
 * @param {number} value - The percentage value (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === 0) return '0%';
  return `${value.toFixed(decimals).replace(/\.0$/, '')}%`;
};

/**
 * Get the appropriate number of decimal places based on the magnitude
 * @param {number} num - The number to analyze
 * @returns {number} Recommended decimal places
 */
export const getOptimalDecimals = (num) => {
  const absNum = Math.abs(num);
  
  if (absNum >= 1e15) return 1; // Quadrillions and above: 1 decimal
  if (absNum >= 1e12) return 1; // Trillions: 1 decimal
  if (absNum >= 1e9) return 1; // Billions: 1 decimal
  if (absNum >= 1e6) return 1; // Millions: 1 decimal
  if (absNum >= 1e3) return 1; // Thousands: 1 decimal
  if (absNum >= 100) return 0; // Hundreds: no decimals
  if (absNum >= 1) return 1;   // Units: 1 decimal
  
  return 2; // Less than 1: 2 decimals
};

/**
 * Format a number with optimal decimal places
 * @param {number} num - The number to format
 * @returns {string} Formatted number with optimal decimals
 */
export const formatNumberOptimal = (num) => {
  const decimals = getOptimalDecimals(num);
  return formatNumber(num, decimals);
};

/**
 * Format transaction value (cryptocurrency/token amounts)
 * @param {number} value - The transaction value
 * @param {string} symbol - Token symbol (optional)
 * @returns {string} Formatted transaction value
 */
export const formatTransactionValue = (value, symbol = '') => {
  if (value === 0) return symbol ? `0 ${symbol}` : '0';
  
  const formatted = formatNumberOptimal(value);
  return symbol ? `${formatted} ${symbol}` : formatted;
};

/**
 * Format duration in seconds to a human-readable string
 * Rules:
 * - Under 60 seconds: show in seconds (e.g., "45 sec")
 * - >= 60 seconds but < 60 minutes: show in minutes and seconds (e.g., "5 min 30 sec")
 * - >= 60 minutes: show in hours and minutes (e.g., "2 hr 15 min")
 * 
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0 || isNaN(seconds)) {
    return '0 sec';
  }
  
  const totalSeconds = Math.floor(seconds);
  
  // Under 60 seconds: show in seconds
  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }
  
  // >= 60 seconds but < 60 minutes: show in minutes and seconds
  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    
    if (remainingSeconds === 0) {
      return `${minutes} min`;
    }
    return `${minutes} min ${remainingSeconds} sec`;
  }
  
  // >= 60 minutes: show in hours and minutes
  const hours = Math.floor(totalSeconds / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
};

/**
 * Format duration from hours to a human-readable string
 * Converts hours to seconds first, then formats using formatDuration
 * 
 * @param {number} hours - Duration in hours
 * @returns {string} Formatted duration string
 */
export const formatDurationFromHours = (hours) => {
  if (!hours || hours === 0 || isNaN(hours)) {
    return '0 sec';
  }
  
  // Convert hours to seconds
  const seconds = hours * 3600;
  return formatDuration(seconds);
};